import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, MessageCircle, BellOff, Bell } from 'lucide-react';
import { Button } from '../ui/button';
import { apiClient } from '../../api';
import { toast } from 'sonner';
import SuperLikeButton from '../SuperLikeButton';
import SafetyMenu from '../SafetyMenu';

const ProfileActions = ({ profile, favorited, actionLoading, onFavorite }) => {
  const [muted, setMuted] = useState(false);
  const [muteBusy, setMuteBusy] = useState(false);

  useEffect(() => {
    apiClient.get(`/notifications/mute/${profile.user_id}`)
      .then((r) => setMuted(!!r.data?.muted))
      .catch(() => {});
  }, [profile.user_id]);

  const toggleMute = async () => {
    setMuteBusy(true);
    try {
      if (muted) {
        await apiClient.delete(`/notifications/mute/${profile.user_id}`);
        setMuted(false);
        toast(`Unmuted ${profile.name}`);
      } else {
        await apiClient.post(`/notifications/mute/${profile.user_id}`);
        setMuted(true);
        toast(`Muted ${profile.name}`, {
          description: 'You won\'t get push notifications from them.',
        });
      }
    } catch (e) {
      toast.error('Could not update mute status');
    } finally { setMuteBusy(false); }
  };

  return (
    <div className="space-y-3 mt-auto">
      <div className="grid grid-cols-2 gap-3" data-testid="profile-actions">
        <Button
          onClick={onFavorite}
          disabled={actionLoading === 'fav'}
          variant="outline"
          data-testid="profile-favorite-btn"
          className={
            favorited
              ? 'h-14 font-semibold bg-fnl-pink text-white border-fnl-pink hover:bg-fnl-pink hover:text-white btn-shadow-pink'
              : 'h-14 font-semibold bg-fnl-purple-light/60 border-purple-700 text-white hover:bg-fnl-purple-light hover:text-white'
          }
        >
          <Bookmark className={`w-5 h-5 mr-1.5 ${favorited ? 'fill-white' : ''}`} />
          {favorited ? 'Saved' : 'Favorite'}
        </Button>
        <Link to={`/chat/${profile.user_id}`} className="block" data-testid="profile-message-link">
          <Button className="w-full h-14 bg-fnl-pink hover:bg-fnl-pink text-white font-semibold btn-shadow-pink">
            <MessageCircle className="w-5 h-5 mr-1.5" />Message
          </Button>
        </Link>
      </div>
      <button
        onClick={toggleMute}
        disabled={muteBusy}
        type="button"
        className={
          muted
            ? 'w-full flex items-center justify-center gap-2 text-xs font-medium py-2 rounded-full bg-fnl-pink/15 text-fnl-pink border border-fnl-pink/40 hover:bg-fnl-pink/25 transition'
            : 'w-full flex items-center justify-center gap-2 text-xs font-medium py-2 rounded-full bg-fnl-purple-light/30 text-purple-300 border border-purple-800/40 hover:text-white hover:bg-fnl-purple-light/50 transition'
        }
        data-testid="profile-mute-btn"
      >
        {muted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
        {muted ? 'Notifications muted — tap to unmute' : 'Mute notifications from this person'}
      </button>
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-purple-900/40">
        <SuperLikeButton targetUserId={profile.user_id} targetName={profile.name} />
        <SafetyMenu targetUserId={profile.user_id} targetName={profile.name} />
      </div>
    </div>
  );
};

export default ProfileActions;
